import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "../api/axiosInstance";
import CommentSection from "../Components/CommentSection";
import { formatDistanceToNow } from "date-fns";
import useAuthStore from "../store/useAuthStore";
import useBlogStore from "../store/useblogStore";
import { FaTrash, FaPencilAlt } from "react-icons/fa";
import { toast } from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import projects from "../Projects/projects";

const CodeBlock = ({ className, children, ...props }) => {
  const [copied, setCopied] = useState(false);
  const codeString = String(children).replace(/\n$/, "");
  const match = /language-(\w+)/.exec(className || "");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codeString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <div className="relative group my-4 rounded-lg overflow-hidden border border-slate-700 bg-slate-950 text-sm">
      <div className="flex justify-between items-center px-4 py-1.5 bg-slate-800 text-xs text-slate-400 font-mono select-none border-b border-slate-700">
        <span>{match ? match[1] : "code"}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="text-cyan-400 hover:text-cyan-300 font-sans transition py-0.5 px-2 rounded hover:bg-slate-700/50"
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto font-mono text-slate-200 leading-relaxed bg-transparent">
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    </div>
  );
};

const getReadingTime = (text) => {
  const words = text ? text.split(/\s+/).length : 0;
  return Math.max(1, Math.round(words / 200));
};

const categoryColors = {
  "Case Study": "bg-purple-950/60 text-purple-200 border-purple-500/40",
  "Tech Journey": "bg-yellow-950/60 text-yellow-200 border-yellow-500/40",
  "Project Deep Dive": "bg-blue-950/60 text-blue-200 border-blue-500/40",
  "General Tech": "bg-slate-800/60 text-slate-200 border-slate-600/40",
};

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { blogs, updateBlogInList } = useBlogStore();
  const { userId, role } = useAuthStore();

  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const [editedTitle, setEditedTitle] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [editedCategory, setEditedCategory] = useState("General Tech");
  const [editedProjectSlug, setEditedProjectSlug] = useState("");
  const [tags, setTags] = useState([]);
  const [editedTags, setEditedTags] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [editTab, setEditTab] = useState("write");
  const [tocHeadings, setTocHeadings] = useState([]);

  // pastel tag color
  const stringToColor = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    return `hsl(${hue}, 70%, 75%)`;
  };

  // Fetch blog (from cache first, then server)
  useEffect(() => {
    let active = true;
    const cached = blogs.find((b) => b._id === id);

    if (cached) {
      setBlog(cached);
      setTags(cached.tags || []);
      setEditedTitle(cached.title);
      setEditedContent(cached.content);
      setEditedCategory(cached.category || "General Tech");
      setEditedProjectSlug(cached.projectSlug || "");
      setLoading(false);
    }

    axios.get(`/blog/${id}`, { withCredentials: true }).then((res) => {
      if (!active) return;
      setBlog(res.data);
      setTags(res.data.tags || []);
      setEditedTitle(res.data.title);
      setEditedContent(res.data.content);
      setEditedCategory(res.data.category || "General Tech");
      setEditedProjectSlug(res.data.projectSlug || "");

      updateBlogInList(res.data); // sync store cache

      setLoading(false);
    });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, updateBlogInList]);

  // Scroll Progress
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (totalScroll > 0) {
        setScrollProgress((window.scrollY / totalScroll) * 100);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // TOC Heading Parser
  useEffect(() => {
    if (blog && blog.content) {
      const headingRegex = /^##\s+(.+)$/gm;
      const headings = [];
      let match;
      while ((match = headingRegex.exec(blog.content)) !== null) {
        const headingText = match[1].trim();
        const slug = headingText.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
        headings.push({ text: headingText, slug });
      }
      setTocHeadings(headings);
    }
  }, [blog]);

  // DELETE Blog
  const handleDelete = async () => {
    try {
      await axios.delete(`/blog/${id}`, { withCredentials: true });
      toast.success("Blog deleted successfully!");
      navigate("/blog");
    } catch (error) {
      toast.error("Failed to delete blog");
    }
  };

  // UPDATE Blog
  const handleUpdate = async () => {
    try {
      const updated = await axios.put(
        `/blog/${id}`,
        {
          title: editedTitle,
          content: editedContent,
          tags: tags,
          category: editedCategory,
          projectSlug: editedProjectSlug,
        },
        { withCredentials: true }
      );

      setBlog(updated.data);
      updateBlogInList(updated.data); // sync store cache

      setEditing(false);
      toast.success("Blog updated successfully!");
    } catch (error) {
      toast.error("Failed to update blog");
    }
  };

  const isOwnerOrAdmin =
    blog?.author?._id === userId || role === "admin";

  const shareUrl = window.location.href;

  // Next / Previous Navigation links
  const currentIndex = blogs.findIndex((b) => b._id === id);
  const nextBlog = currentIndex > 0 ? blogs[currentIndex - 1] : null;
  const prevBlog = currentIndex !== -1 && currentIndex < blogs.length - 1 ? blogs[currentIndex + 1] : null;

  // Linked Project object
  const linkedProject = blog?.projectSlug ? projects.find((p) => p.id === blog.projectSlug) : null;

  // ---------------------------
  // UI
  // ---------------------------

  if (loading) {
    return (
      <div className="grid gap-6 m-6 p-4">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            className="bg-[#1a1a1a] opacity-80 rounded-lg shadow-md p-6 animate-pulse"
          >
            <div className="h-6 w-3/5 bg-gray-600 mb-4 rounded"></div>
            <div className="h-4 w-full bg-gray-700 mb-2 rounded"></div>
            <div className="h-4 w-4/5 bg-gray-700 mb-2 rounded"></div>
            <div className="h-4 w-2/5 bg-gray-600 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!blog)
    return (
      <div className="text-white text-center mt-10">Blog not found.</div>
    );

  return (
    <>
      {/* Scroll Progress Indicator */}
      <div 
        className="fixed top-0 left-0 h-1 bg-cyan-500 z-50 transition-all duration-100 ease-out" 
        style={{ width: `${scrollProgress}%` }}
      />

      <div className="max-w-7xl mx-auto mt-20 px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          
          {/* Left Side: Article Content (3/4 on large screens) */}
          <div className="w-full lg:w-3/4 bg-[#151515] text-gray-100 p-6 sm:p-8 md:p-12 rounded-2xl shadow-xl border border-white/10">
            
            {/* Badges and Actions */}
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
              {/* Category tag */}
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${categoryColors[blog.category || "General Tech"]}`}>
                {blog.category || "General Tech"}
              </span>

              {isOwnerOrAdmin && (
                <div className="flex items-center gap-4">
                  {editing ? (
                    <>
                      <button
                        onClick={handleUpdate}
                        className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium py-1.5 px-4 rounded-md transition"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditedTitle(blog.title);
                          setEditedContent(blog.content);
                          setEditedCategory(blog.category || "General Tech");
                          setEditedProjectSlug(blog.projectSlug || "");
                          setTags(blog.tags || []);
                          setEditing(false);
                        }}
                        className="bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium py-1.5 px-4 rounded-md transition"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <FaPencilAlt
                        onClick={() => setEditing(true)}
                        className="cursor-pointer text-blue-405 hover:text-blue-400 transition"
                      />
                      <FaTrash
                        onClick={() => setShowModal(true)}
                        className="cursor-pointer text-red-500 hover:text-red-400 transition"
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Title / Editor dropdowns */}
            <div className="mb-4">
              {editing ? (
                <div className="flex flex-col gap-4 w-full">
                  <input
                    type="text"
                    className="text-3xl md:text-4xl font-bold bg-slate-900/60 border border-slate-700 rounded-md p-3 outline-none w-full focus:border-cyan-500"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    placeholder="Blog Title"
                  />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-gray-400 block mb-1">Category</label>
                      <select
                        value={editedCategory}
                        onChange={(e) => setEditedCategory(e.target.value)}
                        className="px-3 py-2.5 bg-slate-900 border border-slate-700 rounded-md text-white w-full text-sm focus:outline-none focus:border-cyan-500"
                      >
                        <option value="General Tech">General Tech</option>
                        <option value="Case Study">Case Study</option>
                        <option value="Tech Journey">Tech Journey</option>
                        <option value="Project Deep Dive">Project Deep Dive</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-400 block mb-1">Link to Project</label>
                      <select
                        value={editedProjectSlug}
                        onChange={(e) => setEditedProjectSlug(e.target.value)}
                        className="px-3 py-2.5 bg-slate-900 border border-slate-700 rounded-md text-white w-full text-sm focus:outline-none focus:border-cyan-500"
                      >
                        <option value="">-- No Linked Project --</option>
                        {projects.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ) : (
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-tight">{blog.title}</h1>
              )}
            </div>

            {/* Post details header */}
            <p className="text-sm text-gray-300 mb-6 flex flex-wrap items-center gap-2 border-b border-white/10 pb-4">
              <span>Posted {formatDistanceToNow(new Date(blog.createdAt))} ago</span>
              <span>•</span>
              <span className="flex items-center gap-1 font-semibold">⏱️ {getReadingTime(blog.content)} min read</span>
            </p>

            {/* Linked Project Alert Banner */}
            {!editing && linkedProject && (
              <div className="mb-6 p-4 rounded-xl border border-cyan-500/20 bg-cyan-950/20 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <span className="text-xs text-cyan-400 font-bold uppercase tracking-wider block mb-1">Linked Project Walkthrough</span>
                  <span className="text-sm text-gray-300">
                    This article explains the engineering implementations behind <strong>{linkedProject.title}</strong>.
                  </span>
                </div>
                <Link 
                  to={`/projectsDetails/${linkedProject.id}`} 
                  className="text-sm px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-semibold transition whitespace-nowrap self-stretch sm:self-auto text-center"
                >
                  View Project Case Study &rarr;
                </Link>
              </div>
            )}

            {/* Content (or Writer panel) */}
            {editing ? (
              <div className="flex flex-col gap-4">
                <div className="flex gap-2 border-b border-white/10 pb-2">
                  <button
                    type="button"
                    onClick={() => setEditTab("write")}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                      editTab === "write"
                        ? "bg-cyan-600 text-white"
                        : "bg-transparent text-gray-400 hover:text-white"
                    }`}
                  >
                    Write
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditTab("preview")}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                      editTab === "preview"
                        ? "bg-cyan-600 text-white"
                        : "bg-transparent text-gray-400 hover:text-white"
                    }`}
                  >
                    Preview
                  </button>
                </div>
                {editTab === "write" ? (
                  <textarea
                    className="w-full bg-slate-900/60 border border-slate-700 rounded-md p-4 text-gray-200 h-80 resize-vertical focus:outline-none focus:border-cyan-500 font-mono text-sm leading-relaxed"
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                  />
                ) : (
                  <div className="prose prose-invert max-w-none bg-slate-900/30 border border-slate-800 rounded-md p-4 min-h-[20rem] overflow-y-auto">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h2({ node, children, ...props }) {
                          const text = String(children);
                          const id = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
                          return (
                            <h2 id={id} className="text-2xl font-bold mt-8 mb-4 text-cyan-400 scroll-mt-24" {...props}>
                              {children}
                            </h2>
                          );
                        },
                        code({ className, children, ...props }) {
                          const isInline = !className;
                          return isInline ? (
                            <code className="bg-slate-800 px-1.5 py-0.5 rounded text-cyan-300 font-mono text-sm" {...props}>
                              {children}
                            </code>
                          ) : (
                            <CodeBlock className={className} {...props}>
                              {children}
                            </CodeBlock>
                          );
                        },
                      }}
                    >
                      {editedContent}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            ) : (
              <div className="prose prose-invert max-w-none mb-6">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h2({ node, children, ...props }) {
                      const text = String(children);
                      const id = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
                      return (
                        <h2 id={id} className="text-2xl font-bold mt-8 mb-4 text-cyan-400 scroll-mt-24" {...props}>
                          {children}
                        </h2>
                      );
                    },
                    code({ className, children, ...props }) {
                      const isInline = !className;
                      return isInline ? (
                        <code className="bg-slate-800 px-1.5 py-0.5 rounded text-cyan-300 font-mono text-sm" {...props}>
                          {children}
                        </code>
                      ) : (
                        <CodeBlock className={className} {...props}>
                          {children}
                        </CodeBlock>
                      );
                    },
                  }}
                >
                  {blog.content}
                </ReactMarkdown>
              </div>
            )}

            {/* Tags */}
            {editing ? (
              <div className="mt-6 border-t border-white/5 pt-4">
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="text"
                    className="w-full bg-slate-900 border border-slate-700 rounded-md p-2.5 text-gray-200 text-sm focus:outline-none focus:border-cyan-500"
                    placeholder="Add tag and press Enter"
                    value={editedTags}
                    onChange={(e) => setEditedTags(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const t = editedTags.trim();
                        if (t && !tags.includes(t)) {
                          setTags((prev) => [...prev, t]);
                          setEditedTags("");
                        }
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const t = editedTags.trim();
                      if (t && !tags.includes(t)) {
                        setTags((prev) => [...prev, t]);
                        setEditedTags("");
                      }
                    }}
                    className="bg-cyan-600 hover:bg-cyan-700 text-sm px-4 py-2 text-white rounded-md transition"
                  >
                    Add
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-1 px-3 py-1 bg-cyan-900/40 text-cyan-300 border border-cyan-500/30 rounded-full text-xs"
                    >
                      #{tag}
                      <button
                        onClick={() =>
                          setTags((prev) => prev.filter((t) => t !== tag))
                        }
                        className="text-cyan-400 ml-1 hover:text-red-300 font-bold"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 mt-6 border-t border-white/10 pt-4">
                {blog.tags?.map((tag, i) => (
                  <Link
                    to={`/blog?tag=${encodeURIComponent(tag)}`}
                    key={i}
                    className="px-3 py-1 rounded-full text-xs font-medium text-white border border-white/10"
                    style={{ backgroundColor: stringToColor(tag) }}
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            )}

            <p className="text-sm text-gray-300 mb-4 font-semibold mt-6">
              Author: {blog.author?.name || "Chahat Jain"}
            </p>

            {/* Share */}
            <div className="flex flex-wrap items-center gap-4 mt-6 border-t border-white/10 pt-6">
              <strong className="text-sm text-gray-300">Share:</strong>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl);
                  toast.success("Link copied!");
                }}
                className="px-4 py-2 text-sm bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition"
              >
                Copy Link
              </button>
            </div>

            {/* Next / Previous Article Navigation */}
            <div className="grid sm:grid-cols-2 gap-4 mt-12 border-t border-white/10 pt-8">
              {prevBlog ? (
                <Link 
                  to={`/blog/${prevBlog._id}`} 
                  className="p-4 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 transition block text-left group-link"
                >
                  <span className="text-xs text-cyan-400 font-bold block uppercase tracking-wider mb-1">&larr; Previous Article</span>
                  <span className="text-sm text-gray-200 font-medium line-clamp-1">{prevBlog.title}</span>
                </Link>
              ) : (
                <div />
              )}
              {nextBlog ? (
                <Link 
                  to={`/blog/${nextBlog._id}`} 
                  className="p-4 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 transition block text-right group-link"
                >
                  <span className="text-xs text-cyan-400 font-bold block uppercase tracking-wider mb-1">Next Article &rarr;</span>
                  <span className="text-sm text-gray-200 font-medium line-clamp-1">{nextBlog.title}</span>
                </Link>
              ) : (
                <div />
              )}
            </div>

            {/* Comment section */}
            <div className="mt-12 border-t border-white/10 pt-8">
              <CommentSection blogId={id} />
            </div>

          </div>

          {/* Right Side: Sticky TOC Sidebar (1/4 width on desktop) */}
          <div className="hidden lg:block w-1/4 sticky top-24">
            <div className="p-6 rounded-2xl bg-[#151515] border border-white/10 shadow-lg">
              <h4 className="text-xs font-bold text-gray-300 uppercase tracking-widest mb-4 pb-2 border-b border-white/10">Table of Contents</h4>
              {tocHeadings.length > 0 ? (
                <ul className="space-y-3.5">
                  {tocHeadings.map((h, i) => (
                    <li key={i}>
                      <a 
                        href={`#${h.slug}`} 
                        className="text-sm text-gray-300 hover:text-cyan-300 transition block leading-relaxed hover:translate-x-1 duration-200 transform"
                      >
                        {h.text}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-500 italic">No structure headers found in this post.</p>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Delete Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
          <div className="bg-[#1a1a1a] text-white p-6 rounded-xl shadow-lg border border-white/10 max-w-sm w-full">
            <h2 className="text-xl font-semibold mb-4 text-cyan-400">Confirm Delete</h2>
            <p className="text-sm text-gray-300 mb-6">
              Are you sure you want to delete this blog post? This action is permanent and cannot be undone.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium transition"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BlogDetails;
